import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import TButton from "@/components/TButton";
import Terminal from "@/components/Terminal";
import {
  Zap, Diamond, XCircle, RefreshCw, ArrowUpRight, FileText, Ban,
  Activity, Loader2, AlarmClock, AlertTriangle, Wifi, WifiOff, Server,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ACTION_BUTTONS = [
  { key: "scan", label: "SCAN", icon: Zap, variant: "primary" },
  { key: "enum", label: "ENUM", icon: Diamond, variant: "ghost" },
  { key: "exploit", label: "EXPLOIT", icon: XCircle, variant: "ghost" },
  { key: "pivot", label: "PIVOT", icon: RefreshCw, variant: "ghost" },
  { key: "exfil", label: "EXFIL-SIM", icon: ArrowUpRight, variant: "ghost" },
  { key: "report", label: "REPORT", icon: FileText, variant: "ghost" },
  { key: "abort", label: "ABORT", icon: Ban, variant: "danger" },
];

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [scannerHealth, setScannerHealth] = useState(null);
  const [target] = useState("192.168.1.0/24");
  const [terminalLines, setTerminalLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeMode, setActiveMode] = useState("scan");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data: payload } = await api.get("/dashboard/overview");
    setData(payload);
    try {
      const r = await api.get("/schedules/health");
      setHealth(r.data);
    } catch {
      setHealth(null);
    }
    try {
      const r = await api.get("/system/scanner-health");
      setScannerHealth(r.data);
    } catch {
      setScannerHealth(null);
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const runScan = async (mode) => {
    setActiveMode(mode);
    if (mode === "abort") {
      setTerminalLines((prev) => [...prev, { type: "err", text: "[!] Operation aborted by operator" }]);
      setBusy(false);
      return;
    }
    if (mode === "report") {
      nav("/reports");
      return;
    }
    setBusy(true);
    setTerminalLines([{ type: "cmd", text: `${mode} --target ${target}` }]);
    try {
      const { data: scan } = await api.post("/recon/scan", { target, mode });
      const url = `${API_BASE}/recon/terminal/${scan.id}`;
      const es = new EventSource(url, { withCredentials: true });
      es.onmessage = (ev) => {
        try {
          const j = JSON.parse(ev.data);
          if (j.line) {
            setTerminalLines((prev) => [...prev, { type: "out", text: j.line }]);
          }
          if (j.done) {
            es.close();
            setBusy(false);
            toast.success(`${mode.toUpperCase()} cycle complete`);
          }
        } catch (err) {
          console.error("[dashboard] SSE parse error:", err);
        }
      };
      es.onerror = () => {
        es.close();
        setBusy(false);
      };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed (RBAC?)");
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-cyan-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const score = data.threat_score;
  const isCrit = score >= 75;

  return (
    <div className="grid grid-cols-12 gap-0 min-h-[calc(100vh-3rem)]">
      {/* Center command */}
      <section className="col-span-12 lg:col-span-8 p-6 border-r border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400">Command Console</div>
            <h1 className="font-display text-2xl mt-1 tracking-wide">Operations Dashboard</h1>
          </div>
          <div className="text-xs text-slate-400 uppercase tracking-widest">
            TARGET <span className="text-cyan-300 ml-2">{target}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
          {ACTION_BUTTONS.map((b) => (
            <TButton
              key={b.key}
              data-testid={`action-${b.key}`}
              icon={b.icon}
              variant={activeMode === b.key && b.key !== "abort" ? "primary" : b.variant}
              onClick={() => runScan(b.key)}
              disabled={busy && b.key !== "abort"}
              className="w-full"
            >
              {b.label}
            </TButton>
          ))}
        </div>

        <Terminal lines={terminalLines} height="h-[calc(100vh-22rem)]" />

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[
            ["Assets", data.assets_total, "cyan"],
            ["Vulns", data.vulns_total, "fuchsia"],
            ["Critical", data.vulns_by_severity.critical, "rose"],
            ["Open Incidents", data.open_incidents, "amber"],
          ].map(([label, v, c]) => (
            <div key={label} className="csx-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
              <div className={`mt-1 text-2xl font-bold text-${c}-400`}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Right metrics */}
      <aside className="col-span-12 lg:col-span-4 p-6 space-y-6 bg-slate-950/30">
        {/* Threat Score */}
        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Threat Score</div>
          <div className="flex items-end gap-2 mt-2">
            <div data-testid="threat-score" className={`font-display text-7xl font-black ${isCrit ? "text-rose-400 text-glow-danger" : "text-amber-400"}`}>
              {score}
            </div>
            <div className="pb-3 text-xs text-slate-400">/ 100</div>
          </div>
          <div className={`mt-1 text-xs tracking-widest uppercase ${isCrit ? "text-rose-300" : "text-amber-300"}`}>
            {data.risk_level} RISK
          </div>
          <div className="mt-3 h-1.5 bg-slate-800">
            <div
              className={`h-full ${isCrit ? "bg-gradient-to-r from-rose-500 to-amber-400" : "bg-gradient-to-r from-amber-400 to-emerald-400"}`}
              style={{ width: `${score}%` }}
            />
          </div>
        </div>

        {/* Module status */}
        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Module Status</div>
          <ul className="space-y-2.5">
            {data.modules.map((m) => {
              const dot =
                m.color === "success" ? "bg-emerald-400"
                : m.color === "primary" ? "bg-cyan-400"
                : m.color === "warning" ? "bg-amber-400"
                : "bg-rose-400";
              const txt =
                m.color === "success" ? "text-emerald-300"
                : m.color === "primary" ? "text-cyan-300"
                : m.color === "warning" ? "text-amber-300"
                : "text-rose-300";
              return (
                <li key={m.name} data-testid={`module-${m.name.toLowerCase().replace(/ /g, "-")}`} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${dot} pulse-dot`} />
                    <span className="text-slate-200">{m.name}</span>
                  </div>
                  <span className={`text-[10px] tracking-[0.2em] uppercase ${txt}`}>{m.status}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Compliance */}
        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Compliance Coverage</div>
          <div className="grid grid-cols-2 gap-2">
            {data.compliance.slice(0, 4).map((c, i) => {
              const v = c.coverage;
              const col = v >= 90 ? "text-emerald-400" : v >= 75 ? "text-amber-400" : "text-rose-400";
              return (
                <div key={c.framework} className="border border-slate-800 p-3 bg-slate-950/40">
                  <div className={`font-display text-2xl font-black ${col}`}>{v}%</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400 mt-0.5">{c.framework}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scanner Backend Health */}
        {scannerHealth && (
          <div data-testid="scanner-health-widget" className="csx-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-400">
                <Server className="h-3 w-3 text-cyan-400" />
                Scanner Backends
              </div>
              <span
                data-testid="scanner-mode"
                className={[
                  "text-[10px] uppercase tracking-widest px-1.5 py-0.5 border font-bold",
                  scannerHealth.mode === "live"
                    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                    : "border-amber-500/60 text-amber-300 bg-amber-500/10",
                ].join(" ")}
              >
                {scannerHealth.mode}
              </span>
            </div>
            <div className="space-y-2 text-[11px]">
              {[
                { key: "nuclei", label: "Nuclei" },
                { key: "gvm",    label: "Greenbone GVM" },
              ].map(({ key, label }) => {
                const b = scannerHealth[key] || {};
                const Icon = b.ok ? Wifi : WifiOff;
                const tone = b.ok ? "text-emerald-400"
                           : b.configured ? "text-rose-400"
                           : "text-slate-500";
                return (
                  <div
                    key={key}
                    data-testid={`backend-${key}`}
                    className="flex items-start gap-2 border border-slate-800 p-2 bg-slate-950/40"
                  >
                    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${tone}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-100 font-semibold">{label}</span>
                        <span className={`text-[9px] uppercase tracking-widest font-mono ${tone}`}>
                          {b.ok ? "live" : (b.configured ? "down" : "not configured")}
                        </span>
                      </div>
                      {b.version && <div className="text-[10px] text-slate-500 font-mono mt-0.5">{b.version}</div>}
                      {b.detail && <div className="text-[10px] text-slate-500 mt-0.5 break-words">{b.detail}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {scannerHealth.mode === "simulated" && (
              <div className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                Running on synthetic findings. Deploy <span className="font-mono text-cyan-300">lane-b-compose.yml</span> to a VM and set <span className="font-mono text-cyan-300">NUCLEI_BIN</span> / <span className="font-mono text-cyan-300">GVM_HOST</span> to flip to live scans.
              </div>
            )}
          </div>
        )}

        {/* Schedule Health */}
        {health && health.total > 0 && (
          <div data-testid="schedule-health-widget" className="csx-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-400">
                <AlarmClock className="h-3 w-3 text-cyan-400" />
                Schedule Health
              </div>
              <button onClick={() => nav("/scans")} className="text-[10px] uppercase tracking-widest text-cyan-300 hover:text-cyan-200">
                manage →
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="border border-slate-800 p-2 bg-slate-950/40">
                <div data-testid="sched-health-healthy" className="font-display text-xl font-black text-emerald-300">{health.healthy}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400 mt-0.5">healthy</div>
              </div>
              <div className="border border-slate-800 p-2 bg-slate-950/40">
                <div data-testid="sched-health-paused" className="font-display text-xl font-black text-amber-300">{health.paused}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400 mt-0.5">paused</div>
              </div>
              <div className="border border-slate-800 p-2 bg-slate-950/40">
                <div data-testid="sched-health-skipped" className={[
                  "font-display text-xl font-black",
                  health.skipped.length > 0 ? "text-rose-300" : "text-slate-500",
                ].join(" ")}>{health.skipped.length}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400 mt-0.5">skipped</div>
              </div>
            </div>
            {(health.skipped.length > 0 || health.overdue.length > 0) && (
              <ul className="space-y-1.5 text-[11px] max-h-32 overflow-y-auto">
                {health.skipped.slice(0, 4).map((s) => (
                  <li key={`sk-${s.id}`} data-testid={`sched-skip-${s.id}`} className="flex items-start gap-2 text-slate-300 leading-snug">
                    <AlertTriangle className="h-3 w-3 text-rose-400 mt-0.5 shrink-0" />
                    <span className="truncate">
                      <span className="text-slate-100">{s.name}</span>
                      <span className="text-slate-500"> · {s.engagement_name}</span>
                      <span className="text-rose-300 font-mono ml-1">[{s.last_skip_reason}]</span>
                    </span>
                  </li>
                ))}
                {health.overdue.slice(0, 2).map((s) => (
                  <li key={`od-${s.id}`} className="flex items-start gap-2 text-slate-300 leading-snug">
                    <AlarmClock className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" />
                    <span className="truncate">
                      <span className="text-slate-100">{s.name}</span>
                      <span className="text-amber-300 font-mono ml-1">overdue 10m+</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Live Activity */}
        <div className="csx-card p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">
            <Activity className="h-3 w-3 text-cyan-400" />
            Live Activity Feed
          </div>
          <ul className="space-y-1.5 text-[11px] font-mono max-h-48 overflow-y-auto pr-1">
            {data.activity.map((a) => (
              <li key={a.msg} className="text-slate-400 leading-snug">
                <span className="text-cyan-400">›</span> {a.msg}
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
};

export default Dashboard;
